/**
 * BusinessDashboard — clean, customizable business-owner dashboard.
 *
 * - 8 cards: Needs Attention, Bank Accounts, Cash Balance, Money In/Out,
 *   Profit & Loss, Expenses, Sales Paid Funnel, AI Assistant.
 * - Drag-to-reorder + hide + resize via react-grid-layout, persisted to
 *   /api/dashboard/layout (workspace + user scoped).
 * - Date range filter (this month / last month / Q-to-date / YTD).
 * - Wires to existing endpoints — does NOT duplicate accounting logic.
 *
 * Design language:
 *   - Soft white-on-emerald palette pulled from CSS vars (--primary etc).
 *   - Large readable numbers via tabular-nums.
 *   - Soft shadows, rounded-2xl, subtle borders.
 *   - Works in light + dark mode.
 */
import { useEffect, useState, useCallback } from 'react';
import { Responsive, useContainerWidth } from 'react-grid-layout';
import { useWorkspace } from '../context/WorkspaceContext';
import { apiFetch } from '../fetchInterceptor';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  AlertCircle, ArrowDownRight, ArrowUpRight, Banknote, BarChart3,
  CheckCircle2, ChevronDown, Eye, EyeOff, Grip, Landmark, LayoutGrid,
  Loader2, Pin, RotateCcw, Sparkles, TrendingUp, Wallet,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

const API = process.env.REACT_APP_BACKEND_URL;

const fmtUSD = (n, opts = {}) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD',
    maximumFractionDigits: opts.cents ? 2 : 0,
  }).format(Number(n) || 0);

const DATE_RANGES = [
  { id: 'this_month', label: 'This month' },
  { id: 'last_month', label: 'Last month' },
  { id: 'qtd', label: 'Quarter to date' },
  { id: 'ytd', label: 'Year to date' },
  { id: 'last_30_days', label: 'Last 30 days' },
];

// Default 8-card layout. Stored under react-grid-layout's required
// {i, x, y, w, h} shape so it round-trips through the existing
// /api/dashboard/layout endpoint without translation.
const DEFAULT_CARDS = [
  { i: 'cash_balance',      x: 0, y: 0, w: 3,  h: 3, label: 'Cash Balance',         visible: true },
  { i: 'money_in_out',      x: 3, y: 0, w: 3,  h: 3, label: 'Money In / Out',       visible: true },
  { i: 'needs_attention',   x: 6, y: 0, w: 6,  h: 6, label: 'Needs Attention',      visible: true },
  { i: 'sales_funnel',      x: 0, y: 3, w: 6,  h: 3, label: 'Sales — Paid Funnel',  visible: true },
  { i: 'profit_loss',       x: 0, y: 6, w: 6,  h: 6, label: 'Profit & Loss',        visible: true },
  { i: 'expenses',          x: 6, y: 6, w: 6,  h: 6, label: 'Expenses',             visible: true },
  { i: 'bank_accounts',     x: 0, y: 12, w: 6, h: 5, label: 'Bank Accounts',        visible: true },
  { i: 'ai_assistant',      x: 6, y: 12, w: 6, h: 5, label: 'AI Assistant',         visible: true },
];

const COLS = { lg: 12, md: 12, sm: 6, xs: 4, xxs: 2 };
const BREAKPOINTS = { lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 };

export default function BusinessDashboard() {
  const { currentWorkspace } = useWorkspace();
  const wsId = currentWorkspace?.workspace_id;

  // Server-backed state.
  const [overview, setOverview] = useState(null);
  const [arap, setArAp] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Layout + customization.
  const [cards, setCards] = useState(DEFAULT_CARDS);
  const [customizing, setCustomizing] = useState(false);
  const [dateRange, setDateRange] = useState('this_month');
  const [rangeOpen, setRangeOpen] = useState(false);

  // ─── Load all data ────────────────────────────────────────────────
  const loadAll = useCallback(async () => {
    if (!wsId) return;
    setLoading(true);
    try {
      const [ov, ar, al, ba, layoutRes] = await Promise.all([
        apiFetch(`${API}/api/dashboard/overview?workspace_id=${wsId}`),
        apiFetch(`${API}/api/dashboard/ar-ap?workspace_id=${wsId}`),
        apiFetch(`${API}/api/dashboard/alerts?workspace_id=${wsId}`),
        apiFetch(`${API}/api/banking/accounts?workspace_id=${wsId}`),
        apiFetch(`${API}/api/dashboard/layout?workspace_id=${wsId}`),
      ]);
      if (ov.ok) setOverview(await ov.json());
      if (ar.ok) setArAp(await ar.json());
      if (al.ok) setAlerts(((await al.json()).alerts) || []);
      if (ba.ok) {
        const j = await ba.json();
        setBankAccounts(j.accounts || j || []);
      }
      if (layoutRes.ok) {
        const j = await layoutRes.json();
        const stored = (j.layout || []).filter((c) => c.i?.startsWith('biz_'));
        // Merge stored card overrides on top of DEFAULT_CARDS — never
        // trust the server to be the source of truth for the card
        // catalog itself (new cards added later must still appear).
        const overrides = Object.fromEntries(
          stored.map((c) => [c.i.replace(/^biz_/, ''), c])
        );
        setCards(DEFAULT_CARDS.map((c) => {
          const o = overrides[c.i];
          if (!o) return c;
          return {
            ...c,
            x: o.x, y: o.y, w: o.w, h: o.h,
            visible: o.visible !== false,
          };
        }));
      }
    } catch (e) {
      // Non-fatal: every card has its own empty state.
      console.warn('BusinessDashboard load partial-fail:', e);
    } finally {
      setLoading(false);
    }
  }, [wsId]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // ─── Persist layout ───────────────────────────────────────────────
  const persistLayout = async (next) => {
    if (!wsId) return;
    const payload = next.map((c) => ({
      i: `biz_${c.i}`, x: c.x, y: c.y, w: c.w, h: c.h,
      visible: c.visible !== false,
    }));
    try {
      await apiFetch(`${API}/api/dashboard/layout?workspace_id=${wsId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ layout: payload }),
      });
    } catch (e) {
      toast.error('Couldn\'t save layout.');
    }
  };

  const onLayoutChange = (newLayout) => {
    if (!customizing) return;
    setCards((cur) => {
      const map = Object.fromEntries(newLayout.map((l) => [l.i, l]));
      const next = cur.map((c) => {
        const u = map[c.i];
        return u ? { ...c, x: u.x, y: u.y, w: u.w, h: u.h } : c;
      });
      persistLayout(next);
      return next;
    });
  };

  const toggleCardVisibility = (cardId) => {
    setCards((cur) => {
      const next = cur.map((c) =>
        c.i === cardId ? { ...c, visible: !c.visible } : c
      );
      persistLayout(next);
      return next;
    });
  };

  const resetLayout = () => {
    setCards(DEFAULT_CARDS);
    persistLayout(DEFAULT_CARDS);
    toast.success('Dashboard reset to default.');
  };

  // ─── Render ───────────────────────────────────────────────────────
  const visibleCards = cards.filter((c) => c.visible !== false);
  const currentRangeLabel = DATE_RANGES.find((r) => r.id === dateRange)?.label;

  const kpi = overview?.kpi || {};

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b bg-card/40 backdrop-blur-sm sticky top-0 z-30">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-4 flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">
              Business Dashboard
            </h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              {loading ? 'Loading the latest…' : `Here\u2019s what needs your attention — ${currentRangeLabel.toLowerCase()}.`}
            </p>
          </div>

          {/* Date-range dropdown */}
          <div className="relative">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setRangeOpen((o) => !o)}
              data-testid="dash-range-toggle"
            >
              {currentRangeLabel}
              <ChevronDown className="w-4 h-4 ml-1.5" />
            </Button>
            {rangeOpen && (
              <div className="absolute right-0 mt-1.5 w-44 rounded-lg border bg-popover shadow-lg p-1 z-50">
                {DATE_RANGES.map((r) => (
                  <button
                    key={r.id}
                    onClick={() => { setDateRange(r.id); setRangeOpen(false); }}
                    className={cn(
                      'w-full text-left px-3 py-1.5 rounded-md text-sm hover:bg-muted',
                      dateRange === r.id && 'bg-primary/10 text-primary font-medium'
                    )}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Customize toggle */}
          <Button
            variant={customizing ? 'default' : 'outline'}
            size="sm"
            onClick={() => setCustomizing((c) => !c)}
            data-testid="dash-customize-toggle"
            className={cn(customizing && 'bg-primary text-primary-foreground')}
          >
            <LayoutGrid className="w-4 h-4 mr-1.5" />
            {customizing ? 'Done' : 'Customize'}
          </Button>

          {customizing && (
            <Button variant="ghost" size="sm" onClick={resetLayout} title="Reset to default layout">
              <RotateCcw className="w-4 h-4" />
            </Button>
          )}
        </div>

        {/* Show-hide tray when customizing */}
        {customizing && (
          <div className="max-w-[1600px] mx-auto px-4 sm:px-6 pb-3 flex flex-wrap items-center gap-2">
            <span className="text-xs uppercase tracking-wider text-muted-foreground mr-1">Cards:</span>
            {cards.map((c) => (
              <button
                key={c.i}
                onClick={() => toggleCardVisibility(c.i)}
                className={cn(
                  'inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border transition-colors',
                  c.visible !== false
                    ? 'bg-primary/10 text-primary border-primary/30 hover:bg-primary/15'
                    : 'bg-muted text-muted-foreground border-border hover:bg-muted/70 line-through'
                )}
                data-testid={`dash-card-toggle-${c.i}`}
              >
                {c.visible !== false ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                {c.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Grid */}
      <div className="max-w-[1600px] mx-auto px-2 sm:px-4 py-4">
        <DashboardGrid
          visibleCards={visibleCards}
          customizing={customizing}
          onLayoutChange={onLayoutChange}
          loading={loading}
          kpi={kpi}
          overview={overview}
          arap={arap}
          alerts={alerts}
          bankAccounts={bankAccounts}
        />

        {visibleCards.length === 0 && (
          <div className="py-24 text-center text-muted-foreground">
            <EyeOff className="w-10 h-10 mx-auto mb-3 opacity-50" />
            All cards are hidden. Click <span className="font-medium text-foreground">Customize</span> to bring them back.
          </div>
        )}
      </div>
    </div>
  );
}

// Wraps the responsive grid so we can pipe the container's measured width
// into <Responsive width={…} /> via the lib's useContainerWidth hook
// (replaces the old WidthProvider HOC that was removed in v2.2).
function DashboardGrid({ visibleCards, customizing, onLayoutChange, loading, kpi, overview, arap, alerts, bankAccounts }) {
  // The hook owns its own ref + ResizeObserver and returns a stable
  // containerRef we attach to our wrapper. `mounted` flips true only
  // after the first measurement so we never render <Responsive> with a
  // width of 0 (which would collapse every card to the top-left).
  const { width, containerRef, mounted } = useContainerWidth();
  if (visibleCards.length === 0) return null;
  return (
    <div ref={containerRef} className="w-full">
      {mounted && (
        <Responsive
          className="layout"
          width={width}
          layouts={{ lg: visibleCards, md: visibleCards }}
          breakpoints={BREAKPOINTS}
          cols={COLS}
          rowHeight={60}
          margin={[16, 16]}
          isDraggable={customizing}
          isResizable={customizing}
          onLayoutChange={onLayoutChange}
          draggableHandle=".biz-card-handle"
          compactType="vertical"
        >
          {visibleCards.map((c) => (
            <div key={c.i} data-testid={`dash-card-${c.i}`}>
              <CardShell label={c.label} customizing={customizing}>
                {c.i === 'cash_balance' && <CashBalanceCard kpi={kpi} loading={loading} />}
                {c.i === 'money_in_out' && <MoneyInOutCard kpi={kpi} loading={loading} />}
                {c.i === 'needs_attention' && <NeedsAttentionCard alerts={alerts} arap={arap} loading={loading} />}
                {c.i === 'sales_funnel' && <SalesFunnelCard arap={arap} loading={loading} />}
                {c.i === 'profit_loss' && <ProfitLossCard overview={overview} loading={loading} />}
                {c.i === 'expenses' && <ExpensesCard overview={overview} loading={loading} />}
                {c.i === 'bank_accounts' && <BankAccountsCard accounts={bankAccounts} loading={loading} />}
                {c.i === 'ai_assistant' && <AIAssistantCard />}
              </CardShell>
            </div>
          ))}
        </Responsive>
      )}
    </div>
  );
}

// ─── Reusable card shell ────────────────────────────────────────────
function CardShell({ label, children, customizing }) {
  return (
    <Card className="h-full overflow-hidden shadow-sm hover:shadow-md transition-shadow flex flex-col">
      <CardHeader className={cn(
        'flex flex-row items-center justify-between space-y-0 py-2.5 px-4 border-b bg-muted/30',
        customizing && 'biz-card-handle cursor-grab active:cursor-grabbing'
      )}>
        <h3 className="text-sm font-semibold tracking-tight">{label}</h3>
        {customizing && <Grip className="w-3.5 h-3.5 text-muted-foreground" />}
      </CardHeader>
      <CardContent className="flex-1 overflow-auto p-4">
        {children}
      </CardContent>
    </Card>
  );
}

// ─── Inline sparkline ────────────────────────────────────────────────
// A tiny pure-SVG line chart. Used inside StatBlock to show a 12-month
// trend under the headline number without pulling in Recharts (which
// would add ~80kb just for these). Designed to be readable at <120px
// wide and degrade gracefully when there's only 1-2 data points.
function Sparkline({ data, tone = 'default', height = 28 }) {
  const series = (data || []).filter((n) => Number.isFinite(n));
  if (series.length < 2) {
    return <div style={{ height }} />;
  }
  const min = Math.min(...series);
  const max = Math.max(...series);
  const range = max - min || 1;
  const stroke =
    tone === 'positive' ? 'rgb(16, 185, 129)' :    // emerald-500
    tone === 'negative' ? 'rgb(244, 63, 94)' :     // rose-500
    'currentColor';
  const width = 100; // viewBox units; scales to parent
  const stepX = width / (series.length - 1);
  const points = series.map((v, i) => {
    const x = i * stepX;
    const y = height - 2 - ((v - min) / range) * (height - 4);
    return `${x},${y}`;
  }).join(' ');
  // Build a filled-area path that mirrors the line so we can give the
  // sparkline a soft tinted fill below the curve — matches the
  // existing app's understated chart treatment.
  const area = `M0,${height} L${points.replace(/ /g, ' L')} L${width},${height} Z`;
  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      className="w-full opacity-80"
      style={{ height, color: stroke }}
      aria-hidden="true"
    >
      <path d={area} fill={stroke} fillOpacity="0.10" />
      <polyline points={points} fill="none" stroke={stroke} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

// ─── Individual cards ───────────────────────────────────────────────
function StatBlock({ icon: Icon, label, value, sub, tone = 'default', spark, to }) {
  const valueEl = (
    <div className={cn(
      'text-3xl font-semibold tabular-nums',
      tone === 'positive' && 'text-emerald-600 dark:text-emerald-400',
      tone === 'negative' && 'text-rose-600 dark:text-rose-400',
      to && 'group-hover:underline decoration-2 underline-offset-4 cursor-pointer transition-all'
    )}>
      {value}
    </div>
  );
  const inner = (
    <div className={cn('space-y-1', to && 'group')}>
      <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-muted-foreground">
        {Icon && <Icon className="w-3.5 h-3.5" />}
        {label}
      </div>
      {to ? (
        <Link to={to} className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded">
          {valueEl}
        </Link>
      ) : valueEl}
      {spark && spark.length >= 2 && <Sparkline data={spark} tone={tone} />}
      {sub && <div className="text-xs text-muted-foreground">{sub}</div>}
    </div>
  );
  return inner;
}

function CashBalanceCard({ kpi, loading, sparkData }) {
  return (
    <div className="space-y-2 h-full flex flex-col justify-center">
      <StatBlock
        icon={Wallet}
        label="Cash on hand"
        value={loading ? '…' : fmtUSD(kpi.cash_balance)}
        sub="Across all connected accounts — view transactions →"
        spark={sparkData}
        to="/banking"
      />
    </div>
  );
}

function MoneyInOutCard({ kpi, loading, incomeTrend, expenseTrend }) {
  const delta = kpi.mom_net_income_delta;
  return (
    <div className="grid grid-cols-2 gap-3 h-full content-center">
      <StatBlock
        icon={ArrowUpRight}
        label="Money in"
        value={loading ? '…' : fmtUSD(kpi.ytd_income)}
        tone="positive"
        sub="YTD · view P&L →"
        spark={incomeTrend}
        to="/reports/profit-loss"
      />
      <StatBlock
        icon={ArrowDownRight}
        label="Money out"
        value={loading ? '…' : fmtUSD(kpi.ytd_expenses)}
        tone="negative"
        sub={delta != null ? `${delta >= 0 ? '+' : ''}${delta}% MoM` : 'YTD'}
        spark={expenseTrend}
        to="/reports/profit-loss"
      />
    </div>
  );
}

function NeedsAttentionCard({ alerts, arap, loading }) {
  const items = [];
  (alerts || []).slice(0, 5).forEach((a) => {
    items.push({
      key: `alert_${a.alert_id || a.id}`,
      title: a.title || a.message,
      sub: a.description || a.subtitle,
      link: a.link || a.cta_link,
    });
  });
  if (arap?.ar?.overdue?.amount > 0) {
    items.push({
      key: 'ar_overdue',
      title: `${arap.ar.overdue.count} overdue invoice${arap.ar.overdue.count === 1 ? '' : 's'}`,
      sub: `${fmtUSD(arap.ar.overdue.amount)} unpaid past due date`,
      link: '/invoices?status=overdue',
    });
  }
  if (loading) return <div className="text-sm text-muted-foreground">Loading…</div>;
  if (items.length === 0) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-center text-sm text-muted-foreground py-6">
        <CheckCircle2 className="w-10 h-10 text-emerald-500 mb-2" />
        Nothing on fire today. Nice work.
      </div>
    );
  }
  return (
    <div className="space-y-2">
      {items.map((it) => (
        <Link
          key={it.key}
          to={it.link || '#'}
          className="block rounded-lg border bg-card hover:bg-muted/40 transition-colors px-3 py-2"
        >
          <div className="flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
            <div className="min-w-0">
              <div className="text-sm font-medium truncate">{it.title}</div>
              {it.sub && <div className="text-xs text-muted-foreground truncate">{it.sub}</div>}
            </div>
          </div>
        </Link>
      ))}
    </div>
  );
}

function SalesFunnelCard({ arap, loading }) {
  if (loading) return <div className="text-sm text-muted-foreground">Loading…</div>;
  const ar = arap?.ar || {};
  const paid = ar.collected_ytd || 0;
  const open = ar.outstanding || 0;
  const total = paid + open;
  const paidPct = total > 0 ? Math.round((paid / total) * 100) : 0;
  return (
    <div className="h-full flex flex-col justify-center space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <Link to="/invoices?status=paid" className="block group" data-testid="dash-funnel-paid-link">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">Paid · drill in →</div>
          <div className="text-2xl font-semibold tabular-nums text-emerald-600 dark:text-emerald-400 group-hover:underline decoration-2 underline-offset-4">
            {fmtUSD(paid)}
          </div>
        </Link>
        <Link to="/invoices?status=unpaid" className="block group" data-testid="dash-funnel-unpaid-link">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">Not paid · drill in →</div>
          <div className="text-2xl font-semibold tabular-nums group-hover:underline decoration-2 underline-offset-4">
            {fmtUSD(open)}
          </div>
        </Link>
      </div>
      <div className="space-y-1.5">
        <div className="h-3 rounded-full bg-muted overflow-hidden flex">
          <div
            className="h-full bg-primary transition-all"
            style={{ width: `${paidPct}%` }}
          />
          <div
            className="h-full bg-amber-400 transition-all"
            style={{ width: `${100 - paidPct}%` }}
          />
        </div>
        <div className="text-xs text-muted-foreground">
          {paidPct}% collected · {fmtUSD(total)} invoiced
        </div>
      </div>
    </div>
  );
}

function ProfitLossCard({ overview, loading }) {
  if (loading || !overview) return <div className="text-sm text-muted-foreground">Loading…</div>;
  const kpi = overview.kpi || {};
  const monthly = overview.monthly_trend || [];
  const maxVal = Math.max(1, ...monthly.flatMap((m) => [m.income || 0, m.expenses || 0]));
  const netSpark = monthly.slice(-12).map((m) => (m.net_income ?? ((m.income || 0) - (m.expenses || 0))));
  return (
    <div className="space-y-3 h-full flex flex-col">
      <div className="grid grid-cols-3 gap-3 shrink-0">
        <StatBlock label="Revenue" value={fmtUSD(kpi.ytd_income)} tone="positive" to="/reports/profit-loss" />
        <StatBlock label="Expenses" value={fmtUSD(kpi.ytd_expenses)} tone="negative" to="/reports/profit-loss" />
        <StatBlock
          label="Net income"
          value={fmtUSD(kpi.ytd_net_income)}
          tone={kpi.ytd_net_income >= 0 ? 'positive' : 'negative'}
          spark={netSpark}
          to="/reports/profit-loss"
        />
      </div>
      <div className="flex-1 flex items-end gap-1 pt-2 min-h-[80px]">
        {monthly.slice(-12).map((m, idx) => {
          const incHeight = maxVal > 0 ? Math.max(2, (m.income || 0) / maxVal * 100) : 2;
          const expHeight = maxVal > 0 ? Math.max(2, (m.expenses || 0) / maxVal * 100) : 2;
          return (
            <Link
              key={idx}
              to={`/reports/profit-loss?month=${m.month}`}
              className="flex-1 flex flex-col items-center gap-0.5 hover:opacity-100 opacity-90 transition-opacity cursor-pointer"
              title={`${m.month}: in ${fmtUSD(m.income)}, out ${fmtUSD(m.expenses)} — click to drill in`}
            >
              <div className="flex items-end gap-0.5 w-full h-full">
                <div
                  className="flex-1 bg-primary/70 hover:bg-primary rounded-t-sm transition-colors"
                  style={{ height: `${incHeight}%` }}
                />
                <div
                  className="flex-1 bg-rose-400/70 hover:bg-rose-500 rounded-t-sm transition-colors"
                  style={{ height: `${expHeight}%` }}
                />
              </div>
              <div className="text-[9px] text-muted-foreground">{(m.month || '').slice(5)}</div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

function ExpensesCard({ overview, loading }) {
  if (loading || !overview) return <div className="text-sm text-muted-foreground">Loading…</div>;
  const kpi = overview.kpi || {};
  const expenseTrend = (overview.monthly_trend || []).slice(-12).map((m) => m.expenses || 0);
  return (
    <div className="space-y-3 h-full flex flex-col justify-center">
      <StatBlock
        icon={TrendingUp}
        label="Expenses (YTD)"
        value={fmtUSD(kpi.ytd_expenses)}
        sub={kpi.mom_expense_delta != null ? `${kpi.mom_expense_delta >= 0 ? '+' : ''}${kpi.mom_expense_delta}% vs last month` : null}
        tone="negative"
        spark={expenseTrend}
        to="/reports/profit-loss?focus=expenses"
      />
      <Button asChild variant="outline" size="sm">
        <Link to="/reports/profit-loss?focus=expenses" data-testid="dash-expenses-view-all">
          View category breakdown
        </Link>
      </Button>
    </div>
  );
}

function BankAccountsCard({ accounts, loading }) {
  if (loading) return <div className="text-sm text-muted-foreground">Loading…</div>;
  if (!accounts || accounts.length === 0) {
    return (
      <div className="text-center text-sm text-muted-foreground py-6">
        <Landmark className="w-8 h-8 mx-auto mb-2 opacity-50" />
        No bank accounts connected.
        <div className="mt-3">
          <Button asChild size="sm">
            <Link to="/banking">Connect a bank</Link>
          </Button>
        </div>
      </div>
    );
  }
  return (
    <div className="space-y-2 max-h-full overflow-y-auto">
      {accounts.slice(0, 5).map((a) => (
        <div
          key={a.account_id || a.id}
          className="flex items-center justify-between border rounded-lg px-3 py-2 bg-card"
        >
          <div className="min-w-0">
            <div className="text-sm font-medium truncate">
              {a.account_name || a.name || 'Account'}
            </div>
            <div className="text-xs text-muted-foreground truncate">
              {a.institution_name || a.subtype || 'Bank'}{' · '}
              <span className={cn(
                a.sync_status === 'error' && 'text-rose-500',
                a.sync_status === 'pending' && 'text-amber-500',
              )}>
                {a.sync_status || 'connected'}
              </span>
            </div>
          </div>
          <div className="text-right">
            <div className="text-sm font-semibold tabular-nums">
              {fmtUSD(a.current_balance ?? a.balance)}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function AIAssistantCard() {
  const quickPrompts = [
    "What needs my attention?",
    "Why did expenses increase?",
    "Which invoices are still unpaid?",
    "Summarize my P&L.",
  ];
  return (
    <div className="space-y-3 h-full flex flex-col">
      <div className="flex items-center gap-2 text-sm">
        <Sparkles className="w-4 h-4 text-primary" />
        <span className="font-medium">Ask AI Buck</span>
        <Badge variant="outline" className="text-[10px] ml-auto">Claude 4.5</Badge>
      </div>
      <div className="flex-1 space-y-1.5 overflow-y-auto">
        {quickPrompts.map((q) => (
          <Link
            key={q}
            to={`/cfo-buck?prefill=${encodeURIComponent(q)}`}
            className="block text-sm text-left px-3 py-2 rounded-lg border bg-card hover:bg-muted/40 transition-colors"
            data-testid={`dash-ai-prompt-${q.slice(0, 12)}`}
          >
            {q}
          </Link>
        ))}
      </div>
      <Button asChild size="sm" className="shrink-0">
        <Link to="/cfo-buck" data-testid="dash-ai-open-buck">
          Open AI Buck →
        </Link>
      </Button>
    </div>
  );
}
